import { cloneElement, isValidElement, useState, useEffect, useCallback, useContext, useMemo, useId } from 'react';
import { LinearProgress, TablePagination } from '@mui/material';
import * as api from './listsApi';
import { IpListDetail } from './IpListDetail';
import { AuthContext } from '_store';
import { useFocusTrap } from '_components';
import './lists.css';

export { Lists };

// ── Tiny helper ────────────────────────────────────────────────────
function timeAgo(iso) {
    if (!iso) return '—';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

// ── TabPanel wrapper ───────────────────────────────────────────────
function TabPanel({ children, value, index }) {
    return (
        <div className="pt-6" style={value === index ? undefined : { display: 'none' }}>
            {children}
        </div>
    );
}

// ── Shared UI helpers ──────────────────────────────────────────────
function TabLoading() {
    return (
        <div className="lists-loading">
            <span className="material-symbols-outlined lists-spin" style={{ fontSize: '2rem', color: 'var(--color-accent-teal)' }}>sync</span>
        </div>
    );
}

const activateOnKeyboard = (event, action) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    action();
};

function TabError({ msg, onClose, className = '' }) {
    return (
        <div className={`lists-error-banner ${className}`.trim()}>
            <span className="material-symbols-outlined text-sm">error</span>
            {msg}
            {onClose && (
                <button type="button" className="lists-inline-clear-btn" onClick={onClose} aria-label="Dismiss message">
                    <span className="material-symbols-outlined text-sm">close</span>
                </button>
            )}
        </div>
    );
}

function NativeModal({ title, children, onClose, onConfirm, confirmLabel = 'Confirm', confirmDisabled = false, danger = false }) {
    const titleId = useId();
    const modalRef = useFocusTrap(true, onClose);

    return (
        <div className="lists-modal-overlay" onClick={(event) => {
            if (event.target === event.currentTarget) onClose();
        }}>
            <div
                ref={modalRef}
                className="lists-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                onClick={e => e.stopPropagation()}
            >
                <div className="lists-modal-header">
                    <h3 id={titleId} className="lists-modal-title">{title}</h3>
                    <button type="button" className="lists-icon-btn" onClick={onClose} aria-label={`Close ${title}`}>
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>
                <div className="lists-modal-content">{children}</div>
                <div className="lists-modal-footer">
                    <button type="button" className="lists-modal-cancel" onClick={onClose}>Cancel</button>
                    <button
                        type="button"
                        className={`lists-modal-confirm${danger ? ' danger' : ''}`}
                        onClick={onConfirm}
                        disabled={confirmDisabled}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

function NativeField({ label, required, children }) {
    const generatedId = useId();
    const isControl = isValidElement(children)
        && typeof children.type === 'string'
        && ['input', 'select', 'textarea'].includes(children.type);
    const controlId = isControl ? (children.props.id || generatedId) : undefined;
    const fieldControl = isControl ? cloneElement(children, {
        id: controlId,
        required: required || children.props.required,
    }) : children;

    return (
        <div className="lists-filter-select-wrap lists-full-width">
            <label className="lists-filter-label" htmlFor={controlId}>{label}{required && ' *'}</label>
            {fieldControl}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════
//  MAIN LISTS PAGE
// ════════════════════════════════════════════════════════════════════
function Lists() {
    const [tab, setTab] = useState(0);
    const [stats, setStats] = useState(null);
    const [health, setHealth] = useState(null);
    const [selectedList, setSelectedList] = useState(null);
    const [error, setError] = useState(null);
    const { userRole } = useContext(AuthContext);
    const isAdmin = userRole === 'admin';

    // Lifted filter state — survives detail navigation
    const [ipSearch, setIpSearch] = useState('');
    const [ipCategory, setIpCategory] = useState('');
    const [ipProvider, setIpProvider] = useState('');
    const [ipActive, setIpActive] = useState('');
    const [ipType, setIpType] = useState('');
    const [ipPage, setIpPage] = useState(0);
    const [ipPageSize, setIpPageSize] = useState(20);

    // Load stats + health on mount
    useEffect(() => {
        api.fetchStats().then(setStats).catch(() => setError('Failed to load stats'));
        api.fetchHealth().then(setHealth).catch(() => {});
    }, []);

    // Memoize each tab independently so unrelated state changes don't remount them
    const ipListsTab = useMemo(() => (
        <IpListsTab onSelect={setSelectedList}
            search={ipSearch} setSearch={setIpSearch}
            categoryFilter={ipCategory} setCategoryFilter={setIpCategory}
            providerFilter={ipProvider} setProviderFilter={setIpProvider}
            activeFilter={ipActive} setActiveFilter={setIpActive}
            typeFilter={ipType} setTypeFilter={setIpType}
            page={ipPage} setPage={setIpPage}
            pageSize={ipPageSize} setPageSize={setIpPageSize}
        />
    ), [ipSearch, ipCategory, ipProvider, ipActive, ipType, ipPage, ipPageSize,
        setSelectedList, setIpSearch, setIpCategory, setIpProvider, setIpActive,
        setIpType, setIpPage, setIpPageSize]);

    const myListsTab    = useMemo(() => <MyListsTab onSelect={setSelectedList} />, [setSelectedList]);
    const providersTab  = useMemo(() => <ProvidersTab />, []);
    const categoriesTab = useMemo(() => <CategoriesTab />, []);
    const syncTab       = useMemo(() => <SyncTab />, []);
    const ipSearchTab   = useMemo(() => <IpSearchTab onSelect={setSelectedList} />, [setSelectedList]);

    const tabs = useMemo(() => [
        { label: 'IP Lists',   icon: 'list_alt',   content: ipListsTab },
        { label: 'My Lists',   icon: 'bookmark',   content: myListsTab },
        { label: 'Providers',  icon: 'view_quilt', content: providersTab },
        { label: 'Categories', icon: 'folder',     content: categoriesTab },
        ...(isAdmin ? [{ label: 'Sync', icon: 'sync', content: syncTab }] : []),
        { label: 'IP Search',  icon: 'search',     content: ipSearchTab },
    ], [ipListsTab, myListsTab, providersTab, categoriesTab, isAdmin, syncTab, ipSearchTab]);

    if (selectedList) {
        return <IpListDetail listId={selectedList} onBack={() => setSelectedList(null)} />;
    }

    return (
        <div className="lists-page">
            {/* ── Hero Header ─────────────────────────────────────── */}
            <div className="lists-hero">
                <div>
                    <div className="lists-breadcrumb">
                        <span className="lists-breadcrumb-dot"></span>
                        <span className="lists-breadcrumb-text">IP_LISTS</span>
                    </div>
                    <h1 className="lists-title">IP Lists</h1>
                </div>
                <div className="flex items-center gap-3">
                    {health && (
                        <span className={`lists-health-badge ${health.status === 'ok' ? 'ok' : 'down'}`}>
                            <span className="material-symbols-outlined text-sm">
                                {health.status === 'ok' ? 'check_circle' : 'error'}
                            </span>
                            {health.status === 'ok' ? 'API Healthy' : 'API Down'}
                        </span>
                    )}
                    {isAdmin && (
                        <button className="lists-create-btn" onClick={() => setTab(1)}>
                            <span className="material-symbols-outlined text-sm">add</span>
                            Create New List
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div className="lists-error-banner">
                    <span className="material-symbols-outlined text-sm">error</span>
                    {error}
                    <button onClick={() => setError(null)} className="ml-auto">
                        <span className="material-symbols-outlined text-sm">close</span>
                    </button>
                </div>
            )}

            {/* ── Stats Cards ─────────────────────────────────────── */}
            <StatsCards stats={stats} />

            {/* ── Tab Bar ─────────────────────────────────────────── */}
            <div className="lists-tab-bar">
                <nav className="lists-tab-nav">
                    {tabs.map((t, i) => (
                        <button
                            key={t.label}
                            onClick={() => setTab(i)}
                            className={`lists-tab-btn${tab === i ? ' active' : ''}`}
                        >
                            {t.icon && <span className="material-symbols-outlined text-sm">{t.icon}</span>}
                            {t.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* ── Tab Content ─────────────────────────────────────── */}
            {tabs.map((t, i) => (
                <TabPanel key={t.label} value={tab} index={i}>{t.content}</TabPanel>
            ))}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════
//  STATS CARDS
// ════════════════════════════════════════════════════════════════════
function StatsCards({ stats }) {
    if (!stats) return (
        <div className="lists-stats-grid">
            {['Categories', 'Providers', 'IP Lists', 'IP Entries', 'Last Sync'].map(label => (
                <div key={label} className="lists-stat-card">
                    <div className="lists-stat-icon-row">
                        <span className="material-symbols-outlined lists-stat-icon">hourglass_empty</span>
                        <span className="lists-stat-label">{label}</span>
                    </div>
                    <div className="lists-stat-value">—</div>
                </div>
            ))}
        </div>
    );

    const cards = [
        { label: 'Categories', value: stats.total_categories,                   icon: 'folder',     dark: false },
        { label: 'Providers',  value: stats.total_providers,                    icon: 'view_quilt', dark: false },
        { label: 'IP Lists',   value: stats.total_ip_lists,                     icon: 'assignment', dark: false },
        { label: 'IP Entries', value: stats.total_ip_entries?.toLocaleString(), icon: 'public',     dark: false },
        { label: 'Last Sync',  value: timeAgo(stats.last_sync_at),              icon: 'sync',       dark: true  },
    ];

    return (
        <div className="lists-stats-grid">
            {cards.map(c => (
                <div key={c.label} className={`lists-stat-card${c.dark ? ' dark' : ''}`}>
                    <div className="lists-stat-icon-row">
                        <span className="material-symbols-outlined lists-stat-icon">{c.icon}</span>
                        <span className="lists-stat-label">{c.label}</span>
                    </div>
                    <div className="lists-stat-value">{c.value ?? '—'}</div>
                </div>
            ))}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════
//  IP LISTS TAB
// ════════════════════════════════════════════════════════════════════
function IpListsTab({ onSelect,
    search, setSearch, categoryFilter, setCategoryFilter,
    providerFilter, setProviderFilter, activeFilter, setActiveFilter,
    typeFilter, setTypeFilter, page, setPage, pageSize, setPageSize }) {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [categories, setCategories] = useState([]);
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch filter options once
    useEffect(() => {
        api.fetchCategories().then(r => setCategories(r.data || r || [])).catch(() => {});
        api.fetchProviders().then(r => Array.isArray(r) ? setProviders(r) : setProviders(r.data || [])).catch(() => {});
    }, []);

    // Debounce search input
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        api.fetchIpLists({ page: page + 1, page_size: pageSize, search: debouncedSearch, category_id: categoryFilter, provider_id: providerFilter, is_active: activeFilter })
            .then(r => {
                const data = Array.isArray(r) ? r : Array.isArray(r.data) ? r.data : [];
                const count = Array.isArray(r) ? r.length : (r.total ?? r.count ?? data.length);
                setRows(data);
                setTotal(count);
            })
            .catch((err) => { console.error('[Lists] fetchIpLists error:', err); setError('Failed to load IP lists'); })
            .finally(() => setLoading(false));
    }, [page, pageSize, debouncedSearch, categoryFilter, providerFilter, activeFilter]);

    useEffect(() => { load(); }, [load]);

    const visibleRows = typeFilter === 'synced' ? rows.filter(r => r.provider_id)
        : typeFilter === 'static' ? rows.filter(r => !r.provider_id)
        : rows;

    return (
        <>
            {error && (
                <div className="lists-error-banner mb-4">
                    <span className="material-symbols-outlined text-sm">error</span>
                    {error}
                </div>
            )}

            {/* ── Filters ─────────────────────────────────────────── */}
            <div className="lists-filters">
                <div className="lists-search-wrap">
                    <div className="lists-search-inner">
                        <span className="material-symbols-outlined lists-search-icon">search</span>
                        <input
                            type="text"
                            placeholder="SEARCH LISTS..."
                            value={search}
                            onChange={e => { setSearch(e.target.value); setPage(0); }}
                            className="lists-search-input"
                        />
                    </div>
                </div>
                <div className="lists-filter-select-wrap">
                    <label className="lists-filter-label">Category</label>
                    <select
                        value={categoryFilter}
                        onChange={e => { setCategoryFilter(e.target.value); setPage(0); }}
                        className="lists-filter-select"
                    >
                        <option value="">All</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <div className="lists-filter-select-wrap">
                    <label className="lists-filter-label">Provider</label>
                    <select
                        value={providerFilter}
                        onChange={e => { setProviderFilter(e.target.value); setPage(0); }}
                        className="lists-filter-select"
                    >
                        <option value="">All</option>
                        {providers.map(p => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                    </select>
                </div>
                <div className="lists-filter-select-wrap">
                    <label className="lists-filter-label">Status</label>
                    <select
                        value={activeFilter}
                        onChange={e => { setActiveFilter(e.target.value); setPage(0); }}
                        className="lists-filter-select"
                    >
                        <option value="">All</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                    </select>
                </div>
                <div className="lists-filter-select-wrap">
                    <label className="lists-filter-label">Type</label>
                    <select
                        value={typeFilter}
                        onChange={e => { setTypeFilter(e.target.value); setPage(0); }}
                        className="lists-filter-select"
                    >
                        <option value="">All</option>
                        <option value="synced">Synced</option>
                        <option value="static">Static</option>
                    </select>
                </div>
            </div>

            {loading && <LinearProgress sx={{ mb: 1 }} />}

            {/* ── Table Card ──────────────────────────────────────── */}
            <div className="lists-table-card">
                <div className="lists-table-header">
                    <div className="flex items-center gap-3">
                        <h2 className="lists-table-title">Managed IP Lists</h2>
                        <span className="lists-badge secondary">{total} Total</span>
                    </div>
                </div>

                <div className="lists-table-wrap">
                    <table className="lists-table">
                        <thead>
                            <tr className="lists-table-head-row">
                                <th className="lists-th">Status</th>
                                <th className="lists-th">List Name</th>
                                <th className="lists-th">Provider / Category</th>
                                <th className="lists-th text-right">Records</th>
                                <th className="lists-th">Sync</th>
                                <th className="lists-th">Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={6} className="lists-td text-center py-10 text-on-surface-variant">
                                        No IP lists found
                                    </td>
                                </tr>
                            )}
                            {visibleRows.map(row => (
                                <tr
                                    key={row.id}
                                    className="lists-table-row"
                                    onClick={() => onSelect(row.id)}
                                >
                                    <td className="lists-td">
                                        <SyncStatusBadge status={row.sync_status} hasProvider={!!row.provider_id} updatedAt={row.updated_at} />
                                    </td>
                                    <td className="lists-td">
                                        <div className="lists-row-name">{row.name}</div>
                                        {row.description && (
                                            <div className="lists-row-desc">{row.description}</div>
                                        )}
                                    </td>
                                    <td className="lists-td">
                                        <ProviderCategoryCell row={row} />
                                    </td>
                                    <td className="lists-td text-right">
                                        <span className="lists-row-count">
                                            {row.entry_count?.toLocaleString() ?? '—'}
                                        </span>
                                    </td>
                                    <td className="lists-td">
                                        <span className="lists-row-sync">
                                            {row.provider?.sync_frequency || (row.provider_id ? 'Scheduled' : 'Manual')}
                                        </span>
                                    </td>
                                    <td className="lists-td">
                                        <span className={`lists-row-time${row.sync_status === 'failed' ? ' error' : ''}`}>
                                            {timeAgo(row.updated_at)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="lists-table-footer">
                    <span>Displaying {visibleRows.length} of {total} results</span>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        sx={{ border: 'none', '& .MuiToolbar-root': { minHeight: 0, padding: 0 } }}
                    />
                </div>
            </div>
        </>
    );
}

// ── Sync status badge ──────────────────────────────────────────────
function SyncStatusBadge({ status, hasProvider, updatedAt }) {
    // If status is 'syncing' but updated_at is older than 10 min, treat as completed
    const effectiveStatus = (status === 'syncing' && updatedAt &&
        Date.now() - new Date(updatedAt).getTime() > 10 * 60 * 1000)
        ? 'completed'
        : status;

    // Static lists (no provider) are never synced — don't show sync state
    if (!hasProvider) {
        return (
            <span className="lists-status-badge static">
                <span className="material-symbols-outlined text-base">edit_note</span>
                STATIC
            </span>
        );
    }
    switch (effectiveStatus) {
        case 'completed':
            return (
                <span className="lists-status-badge synced">
                    <span className="material-symbols-outlined text-base" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                    SYNCED
                </span>
            );
        case 'syncing':
            return (
                <span className="lists-status-badge syncing">
                    <span className="material-symbols-outlined text-base lists-spin">sync</span>
                    UPDATING
                </span>
            );
        case 'failed':
            return (
                <span className="lists-status-badge failed">
                    <span className="material-symbols-outlined text-base" style={{ fontVariationSettings: "'FILL' 1" }}>error</span>
                    FAILED
                </span>
            );
        case 'pending':
            return (
                <span className="lists-status-badge pending">
                    <span className="material-symbols-outlined text-base">schedule</span>
                    PENDING
                </span>
            );
        default:
            return <span className="lists-status-badge">{status || '—'}</span>;
    }
}

function ProviderCategoryCell({ row }) {
    const providerName = row?.provider?.display_name || 'Internal Static';
    const categoryName = row?.category?.name || '';
    const normalizedProvider = providerName.trim().toLowerCase();
    const normalizedCategory = categoryName.trim().toLowerCase();
    const showCategory = Boolean(normalizedCategory) && !normalizedProvider.includes(normalizedCategory);

    return (
        <div className="lists-provider-category-cell">
            <span className="lists-provider-category-name">{providerName}</span>
            {showCategory && <div className="lists-row-category">{categoryName}</div>}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════
//  PROVIDERS TAB
// ════════════════════════════════════════════════════════════════════
function ProvidersTab() {
    const { userRole } = useContext(AuthContext);
    const isAdmin = userRole === 'admin';
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toggling, setToggling] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        api.fetchProviders()
            .then(r => setProviders(Array.isArray(r) ? r : r.data || []))
            .catch(() => setError('Failed to load providers'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleToggle = async (id) => {
        setToggling(id);
        try {
            const updated = await api.toggleProvider(id);
            setProviders(prev => prev.map(p => p.id === id ? updated : p));
        } catch { setError('Failed to toggle provider'); }
        setToggling(null);
    };

    if (loading) return <TabLoading />;

    return (
        <>
            {error && <TabError msg={error} onClose={() => setError(null)} />}
            <div className="lists-providers-grid">
                {providers.length === 0 && (
                    <p className="lists-empty-msg">No providers found.</p>
                )}
                {providers.map(p => (
                    <div key={p.id} className="lists-provider-card">
                        <div className="lists-provider-header">
                            <div className="lists-provider-title-group">
                                <span className="lists-provider-name">{p.display_name}</span>
                                <span className="lists-provider-slug">{p.name}</span>
                            </div>
                            <span className="lists-type-badge external">{p.type || 'Feed'}</span>
                        </div>
                        {p.description && (
                            <p className="lists-provider-desc">{p.description}</p>
                        )}
                        <div className="lists-provider-footer">
                            {isAdmin ? (
                                <label className="lists-toggle-label">
                                    <input
                                        type="checkbox"
                                        className="lists-toggle-input"
                                        checked={!!p.is_enabled}
                                        disabled={toggling === p.id}
                                        onChange={() => handleToggle(p.id)}
                                    />
                                    <span className="lists-toggle-track">
                                        <span className="lists-toggle-thumb"></span>
                                    </span>
                                    <span className="lists-toggle-text">{p.is_enabled ? 'Enabled' : 'Disabled'}</span>
                                </label>
                            ) : (
                                <span className={`lists-status-pill ${p.is_enabled ? 'active' : 'inactive'}`}>
                                    {p.is_enabled ? 'Enabled' : 'Disabled'}
                                </span>
                            )}
                            <span className="lists-provider-date">
                                Created {new Date(p.created_at).toLocaleDateString()}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

// ════════════════════════════════════════════════════════════════════
//  CATEGORIES TAB
// ════════════════════════════════════════════════════════════════════
function CategoriesTab() {
    const { userRole } = useContext(AuthContext);
    const isAdmin = userRole === 'admin';

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Create / Edit dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null); // null = create, object = edit
    const [form, setForm] = useState({ name: '', description: '' });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    // Delete confirmation
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        api.fetchCategories()
            .then(r => setCategories(r.data || r || []))
            .catch(() => setError('Failed to load categories'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setEditTarget(null);
        setForm({ name: '', description: '' });
        setSaveError(null);
        setDialogOpen(true);
    };

    const openEdit = (c) => {
        setEditTarget(c);
        setForm({ name: c.name, description: c.description || '' });
        setSaveError(null);
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        setSaveError(null);
        try {
            const body = { name: form.name.trim(), description: form.description.trim() };
            if (editTarget) {
                await api.updateCategory(editTarget.id, body);
            } else {
                await api.createCategory(body);
            }
            setDialogOpen(false);
            load();
        } catch {
            setSaveError(`Failed to ${editTarget ? 'update' : 'create'} category`);
        }
        setSaving(false);
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await api.deleteCategory(deleteTarget.id);
            setDeleteTarget(null);
            load();
        } catch {
            setError('Failed to delete category');
            setDeleteTarget(null);
        }
        setDeleting(false);
    };

    if (loading) return <TabLoading />;

    return (
        <>
            {error && <TabError msg={error} onClose={() => setError(null)} />}

            {isAdmin && (
                <div className="lists-tab-toolbar">
                    <p className="lists-tab-desc">Categories group IP lists by purpose (e.g. Cloud, CDN, Threat).</p>
                    <button className="lists-create-btn" onClick={openCreate}>
                        <span className="material-symbols-outlined text-sm">add</span>
                        Create Category
                    </button>
                </div>
            )}

            <div className="lists-table-card">
                <div className="lists-table-wrap">
                    <table className="lists-table">
                        <thead>
                            <tr className="lists-table-head-row">
                                <th className="lists-th">Name</th>
                                <th className="lists-th">Description</th>
                                <th className="lists-th">Created</th>
                                <th className="lists-th">Updated</th>
                                {isAdmin && <th className="lists-th"></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {categories.length === 0 && (
                                <tr><td colSpan={isAdmin ? 5 : 4} className="lists-td lists-empty-cell">No categories</td></tr>
                            )}
                            {categories.map(c => (
                                <tr key={c.id} className="lists-table-row">
                                    <td className="lists-td"><span className="lists-row-name">{c.name}</span></td>
                                    <td className="lists-td"><span className="lists-row-sync">{c.description || '—'}</span></td>
                                    <td className="lists-td"><span className="lists-row-time">{new Date(c.created_at).toLocaleDateString()}</span></td>
                                    <td className="lists-td"><span className="lists-row-time">{new Date(c.updated_at).toLocaleDateString()}</span></td>
                                    {isAdmin && (
                                        <td className="lists-td text-right lists-no-wrap">
                                            <button className="lists-icon-btn" title="Edit" onClick={() => openEdit(c)}>
                                                <span className="material-symbols-outlined">edit</span>
                                            </button>
                                            <button className="lists-icon-btn danger" title="Delete" onClick={() => setDeleteTarget(c)}>
                                                <span className="material-symbols-outlined">delete</span>
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create / Edit modal */}
            {dialogOpen && (
                <NativeModal
                    title={editTarget ? 'Edit Category' : 'Create Category'}
                    onClose={() => setDialogOpen(false)}
                    onConfirm={handleSave}
                    confirmLabel={saving ? 'Saving…' : (editTarget ? 'Save' : 'Create')}
                    confirmDisabled={saving || !form.name.trim()}
                >
                    {saveError && <TabError msg={saveError} />}
                    <NativeField label="Name" required>
                        <input className="lists-native-input" value={form.name} maxLength={100} autoFocus
                            onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                    </NativeField>
                    <NativeField label="Description">
                        <textarea className="lists-native-input" rows={2} value={form.description}
                            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                    </NativeField>
                </NativeModal>
            )}

            {/* Delete confirmation modal */}
            {deleteTarget && (
                <NativeModal
                    title="Delete Category"
                    onClose={() => setDeleteTarget(null)}
                    onConfirm={handleDelete}
                    confirmLabel={deleting ? 'Deleting…' : 'Delete'}
                    confirmDisabled={deleting}
                    danger
                >
                    <p className="lists-modal-body">Delete <strong>{deleteTarget.name}</strong>? This cannot be undone.</p>
                </NativeModal>
            )}
        </>
    );
}

// ════════════════════════════════════════════════════════════════════
//  SYNC TAB
// ════════════════════════════════════════════════════════════════════
function SyncTab() {
    const [syncStatus, setSyncStatus] = useState([]);
    const [syncLogs, setSyncLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [syncing, setSyncing] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([api.fetchSyncStatus(), api.fetchSyncLogs(50)])
            .then(([status, logs]) => {
                setSyncStatus(Array.isArray(status) ? status : []);
                setSyncLogs(logs.data || logs || []);
            })
            .catch(() => setError('Failed to load sync data'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleSyncAll = async () => {
        setSyncing(true);
        try {
            await api.triggerSyncAll();
            // Refresh after a short delay to let sync start
            setTimeout(load, 2000);
        } catch { setError('Failed to trigger sync'); }
        setSyncing(false);
    };

    const handleSyncProvider = async (providerId) => {
        try {
            await api.triggerSyncProvider(providerId);
            setTimeout(load, 2000);
        } catch { setError('Failed to trigger provider sync'); }
    };

    if (loading) return <TabLoading />;

    return (
        <>
            {error && <TabError msg={error} onClose={() => setError(null)} />}

            <div className="lists-tab-toolbar">
                <h2 className="lists-section-title">Live Sync Status</h2>
                <button className="lists-create-btn" disabled={syncing} onClick={handleSyncAll}>
                    <span className="material-symbols-outlined text-sm">sync</span>
                    {syncing ? 'Starting…' : 'Sync All Providers'}
                </button>
            </div>

            {syncStatus.length > 0 && (
                <div className="lists-sync-status-grid">
                    {syncStatus.map(s => (
                        <div key={s.provider_id} className="lists-sync-card">
                            <div>
                                <span className="lists-row-name">{s.provider_name}</span>
                                <span className={`lists-status-badge lists-status-badge-block ${s.is_syncing ? 'syncing' : 'synced'}`}>
                                    {s.is_syncing
                                        ? <><span className="material-symbols-outlined text-base lists-spin">sync</span> Syncing…</>
                                        : <><span className="material-symbols-outlined text-base" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span> Idle</>
                                    }
                                </span>
                            </div>
                            <button
                                className="lists-icon-btn"
                                title="Trigger sync"
                                disabled={s.is_syncing}
                                onClick={() => handleSyncProvider(s.provider_id)}
                            >
                                <span className="material-symbols-outlined">refresh</span>
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <h2 className="lists-section-title lists-section-title-spaced">Recent Sync Logs</h2>
            <div className="lists-table-card">
                <div className="lists-table-wrap">
                    <table className="lists-table">
                        <thead>
                            <tr className="lists-table-head-row">
                                <th className="lists-th">Status</th>
                                <th className="lists-th">Provider</th>
                                <th className="lists-th text-right">Added</th>
                                <th className="lists-th text-right">Removed</th>
                                <th className="lists-th text-right">Duration</th>
                                <th className="lists-th">Started</th>
                                <th className="lists-th">Message</th>
                            </tr>
                        </thead>
                        <tbody>
                            {syncLogs.length === 0 && (
                                <tr><td colSpan={7} className="lists-td lists-empty-cell">No sync logs</td></tr>
                            )}
                            {syncLogs.map(log => (
                                <tr key={log.id} className="lists-table-row lists-table-row-static">
                                    <td className="lists-td"><SyncStatusBadge status={log.status} /></td>
                                    <td className="lists-td"><span className="lists-row-sync">{log.provider_id?.substring(0, 8)}…</span></td>
                                    <td className="lists-td text-right"><span className="lists-row-count lists-row-count-positive">+{log.entries_added?.toLocaleString()}</span></td>
                                    <td className="lists-td text-right"><span className="lists-row-count lists-row-count-negative">-{log.entries_removed?.toLocaleString()}</span></td>
                                    <td className="lists-td text-right"><span className="lists-row-sync">{log.duration_ms ? `${(log.duration_ms / 1000).toFixed(1)}s` : '—'}</span></td>
                                    <td className="lists-td"><span className="lists-row-time">{timeAgo(log.started_at)}</span></td>
                                    <td className="lists-td"><span className="lists-row-time">{log.message || '—'}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

// ════════════════════════════════════════════════════════════════════
//  MY LISTS TAB
// ════════════════════════════════════════════════════════════════════
function MyListsTab({ onSelect }) {
    const { authUser, userRole } = useContext(AuthContext);
    const ownerId = authUser?.username;
    const isAdmin = userRole === 'admin';

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [categories, setCategories] = useState([]);

    // Create dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', description: '', category_id: '', is_active: true });
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState(null);

    // Delete confirmation
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Close modals on Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            if (createOpen)  { setCreateOpen(false);  return; }
            if (deleteTarget) { setDeleteTarget(null); return; }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [createOpen, deleteTarget]);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        // Admins fetch all user-owned lists (no owner filter); regular users fetch only their own
        const params = isAdmin
            ? { page_size: 200 }
            : { external_owner_id: ownerId, page_size: 100 };
        api.fetchIpLists(params)
            .then(r => {
                const data = Array.isArray(r) ? r : Array.isArray(r.data) ? r.data : [];
                // When admin fetches without filter it returns system lists too; keep only user-owned ones
                setRows(isAdmin ? data.filter(row => row.external_owner_id) : data);
            })
            .catch(() => setError('Failed to load lists'))
            .finally(() => setLoading(false));
    }, [ownerId, isAdmin])

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        api.fetchCategories().then(r => setCategories(r.data || r || [])).catch(() => {});
    }, []);

    const handleCreate = async () => {
        if (!createForm.name.trim()) return;
        setCreating(true);
        setCreateError(null);
        try {
            await api.createIpList({
                name: createForm.name.trim(),
                ...(createForm.description.trim() && { description: createForm.description.trim() }),
                ...(createForm.category_id && { category_id: createForm.category_id }),
                external_owner_id: ownerId,
                is_active: createForm.is_active,
            });
            setCreateOpen(false);
            setCreateForm({ name: '', description: '', category_id: '', is_active: true });
            load();
        } catch {
            setCreateError('Failed to create list');
        }
        setCreating(false);
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await api.deleteIpList(deleteTarget.id);
            setDeleteTarget(null);
            load();
        } catch {
            setError('Failed to delete list');
            setDeleteTarget(null);
        }
        setDeleting(false);
    };

    return (
        <>
            {error && <TabError msg={error} onClose={() => setError(null)} />}

            <div className="lists-tab-toolbar">
                <p className="lists-tab-desc">
                    {isAdmin ? 'All user-owned static IP lists.' : 'Static IP lists managed by you. Click a list to view and add IP entries.'}
                </p>
                <button className="lists-create-btn" onClick={() => setCreateOpen(true)}>
                    <span className="material-symbols-outlined text-sm">add</span>
                    Create List
                </button>
            </div>

            {loading && <div className="lists-progress-bar"><div className="lists-progress-fill"></div></div>}

            <div className="lists-table-card">
                <div className="lists-table-wrap">
                    <table className="lists-table">
                        <thead>
                            <tr className="lists-table-head-row">
                                <th className="lists-th">Name</th>
                                <th className="lists-th">Category</th>
                                <th className="lists-th text-right">Entries</th>
                                <th className="lists-th">Active</th>
                                <th className="lists-th">Created</th>
                                {isAdmin && <th className="lists-th">Owner</th>}
                                <th className="lists-th"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && !loading && (
                                <tr><td colSpan={isAdmin ? 7 : 6} className="lists-td lists-empty-cell">No lists yet — create your first static IP list above.</td></tr>
                            )}
                            {rows.map(row => (
                                <tr
                                    key={row.id}
                                    className="lists-table-row"
                                    onClick={() => onSelect(row.id)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(event) => activateOnKeyboard(event, () => onSelect(row.id))}
                                >
                                    <td className="lists-td">
                                        <div className="lists-row-name">{row.name}</div>
                                        {row.description && <div className="lists-row-desc">{row.description}</div>}
                                    </td>
                                    <td className="lists-td"><span className="lists-row-sync">{row.category?.name || '—'}</span></td>
                                    <td className="lists-td text-right"><span className="lists-row-count">{row.entry_count?.toLocaleString() ?? '—'}</span></td>
                                    <td className="lists-td">
                                        <span className={`lists-status-pill ${row.is_active ? 'active' : 'inactive'}`}>
                                            {row.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="lists-td"><span className="lists-row-time">{timeAgo(row.created_at)}</span></td>
                                    {isAdmin && <td className="lists-td"><span className="lists-row-time">{row.external_owner_id || '—'}</span></td>}
                                    <td className="lists-td text-right" onClick={e => e.stopPropagation()}>
                                        <button className="lists-icon-btn danger lists-row-action" type="button" title="Delete" aria-label={`Delete ${row.name}`} onClick={() => setDeleteTarget(row)}>
                                            <span className="material-symbols-outlined">delete</span>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create modal */}
            {createOpen && (
                <NativeModal
                    title="Create Static IP List"
                    onClose={() => setCreateOpen(false)}
                    onConfirm={handleCreate}
                    confirmLabel={creating ? 'Creating…' : 'Create'}
                    confirmDisabled={creating || !createForm.name.trim()}
                >
                    {createError && <TabError msg={createError} />}
                    <NativeField label="Name" required>
                        <input className="lists-native-input" value={createForm.name} autoFocus
                            onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} />
                    </NativeField>
                    <NativeField label="Description">
                        <textarea className="lists-native-input" rows={2} value={createForm.description}
                            onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))} />
                    </NativeField>
                    <NativeField label="Category">
                        <select className="lists-filter-select lists-full-width"
                            value={createForm.category_id}
                            onChange={e => setCreateForm(f => ({ ...f, category_id: e.target.value }))}>
                            <option value="">None</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </NativeField>
                    <label className="lists-toggle-label">
                        <input type="checkbox" className="lists-toggle-input"
                            checked={createForm.is_active}
                            onChange={e => setCreateForm(f => ({ ...f, is_active: e.target.checked }))} />
                        <span className="lists-toggle-track"><span className="lists-toggle-thumb"></span></span>
                        <span className="lists-toggle-text">Active</span>
                    </label>
                </NativeModal>
            )}

            {/* Delete confirmation modal */}
            {deleteTarget && (
                <NativeModal
                    title="Delete List"
                    onClose={() => setDeleteTarget(null)}
                    onConfirm={handleDelete}
                    confirmLabel={deleting ? 'Deleting…' : 'Delete'}
                    confirmDisabled={deleting}
                    danger
                >
                    <p className="lists-modal-body">Delete <strong>{deleteTarget.name}</strong>? This will also remove all its IP entries and cannot be undone.</p>
                </NativeModal>
            )}
        </>
    );
}

// ════════════════════════════════════════════════════════════════════
//  IP SEARCH TAB
// ════════════════════════════════════════════════════════════════════
function IpSearchTab({ onSelect }) {
    const [ip, setIp] = useState('');
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSearch = async () => {
        if (!ip.trim()) return;
        setLoading(true);
        setError(null);
        try {
            const data = await api.searchIp(ip.trim());
            setResults(data);
        } catch { setError('Search failed'); }
        setLoading(false);
    };

    return (
        <>
            <p className="lists-tab-desc lists-tab-desc-bottom-gap">
                Look up which IP lists contain a specific IP address.
            </p>

            <div className="lists-ip-search-bar">
                <span className="material-symbols-outlined lists-ip-search-icon">search</span>
                <input
                    type="text"
                    className="lists-search-input lists-ip-search-input"
                    placeholder="E.G. 1.2.3.4"
                    value={ip}
                    onChange={e => setIp(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearch()}
                />
                <button
                    onClick={handleSearch}
                    disabled={loading || !ip.trim()}
                    className="lists-ip-search-btn"
                >
                    {loading
                        ? <><span className="material-symbols-outlined text-sm lists-spin">sync</span> Searching…</>
                        : <><span className="material-symbols-outlined text-sm">manage_search</span> Search</>
                    }
                </button>
            </div>

            {error && <TabError msg={error} className="lists-tab-error-top-gap" />}

            {results !== null && (
                <>
                    <p className="lists-tab-desc lists-search-results-caption">
                        Found in <strong>{results.length}</strong> list(s)
                    </p>
                    {results.length > 0 && (
                        <div className="lists-table-card">
                            <div className="lists-table-wrap">
                                <table className="lists-table">
                                    <thead>
                                        <tr className="lists-table-head-row">
                                            <th className="lists-th">List Name</th>
                                            <th className="lists-th">Provider</th>
                                            <th className="lists-th">Category</th>
                                            <th className="lists-th">Matched Entry</th>
                                            <th className="lists-th text-right">Entries</th>
                                            <th className="lists-th">Active</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {results.map((r, idx) => {
                                            const selectable = Boolean(r.id);
                                            return (
                                            <tr
                                                key={r.id || idx}
                                                className="lists-table-row"
                                                onClick={() => selectable && onSelect(r.id)}
                                                role={selectable ? 'button' : undefined}
                                                tabIndex={selectable ? 0 : undefined}
                                                onKeyDown={selectable ? (event) => activateOnKeyboard(event, () => onSelect(r.id)) : undefined}
                                            >
                                                <td className="lists-td"><span className="lists-row-name">{r.name || '—'}</span></td>
                                                <td className="lists-td"><span className="lists-row-sync">{r.provider?.display_name || r.provider_name || '—'}</span></td>
                                                <td className="lists-td"><span className="lists-row-sync">{r.category?.name || r.category_name || '—'}</span></td>
                                                <td className="lists-td"><span className="lists-row-desc" style={{ fontFamily: 'monospace' }}>{r._matched_entry || '—'}</span></td>
                                                <td className="lists-td text-right"><span className="lists-row-count">{r.entry_count?.toLocaleString() ?? '—'}</span></td>
                                                <td className="lists-td">
                                                    <span className={`lists-status-pill ${r.is_active ? 'active' : 'inactive'}`}>
                                                        {r.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );})}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
}
